/**
 * Street construction: where the road is dug up right now.
 *
 * Source is Denver's street occupancy permits — the permit you need to take
 * over part of a street to work on it. Each one carries what the work is and
 * the dates it runs, which is the whole reason it is worth drawing. Roughly
 * 700 are active on any given day.
 *
 * The obvious-looking alternative, `Downtown_Street_Closures`, is a planning
 * layer: "DOTI Shared Street Recommendation" and similar, 27 rows, no dates,
 * no relationship to current conditions. It would have looked authoritative
 * and meant nothing.
 *
 * Amber, matching the instrumentation accent — this is live operational data
 * about the city's surface, in the same family as the incident beacons but
 * deliberately not teal, because nothing here is a crime.
 */

import { PathLayer, ScatterplotLayer } from "@deck.gl/layers";

import { GROUND_LIFT_M } from "../lib/geo.js";
import { footprints } from "../map/footprints.js";
import { Layer } from "./base.js";

/**
 * Clearance above a rooftop, in metres.
 *
 * Sitting exactly on the roof puts the marker in the same plane as the surface
 * it stands on, which z-fights and tears as the camera moves. Two metres is
 * under a storey, so it still reads as belonging to that roof.
 */
const ROOF_CLEARANCE_M = 2;

/** A rooftop height turned into a position to sit at, with clearance. */
const lift = (height) => (height > 0 ? height + ROOF_CLEARANCE_M : 0);


import { palette, themeName } from "../theme/palette.js";
import { OVER_EVERYTHING } from "../map/depth.js";

/** Dimmer amber for permits: same family, but not a hole in the road. */

/** Work ending within this many days is drawn brighter — it is the live edge. */
const ENDING_SOON_DAYS = 7;

const DAY_MS = 86400000;

/** Epoch millis to a plain date, or an em dash. Dates arrive as ArcGIS millis. */
function asDate(ms) {
  if (!ms) return "—";
  const d = new Date(ms);
  return Number.isFinite(d.getTime()) ? d.toISOString().slice(0, 10) : "—";
}

export class StreetWorkLayer extends Layer {
  static key = "streetwork";
  static label = "Road work";
  static description = "Street occupancy permits active today, with dates";

  async load() {
    this.status = "loading";
    try {
      const response = await fetch(`${this.basePath}/streetwork`);
      if (!response.ok) throw new Error(String(response.status));
      const body = await response.json();
      this._works = body.works ?? [];
      // Building permits ride in the same layer: "this street is dug up" and
      // "this building is being worked on" answer the same question — where
      // is the city being changed.
      this._permits = body.permits ?? [];
      this._serverNow = body.server_now ?? Date.now();
      // Ready if EITHER half arrived. Judging by works alone dated from
      // before permits rode in this layer, and it made an upstream outage
      // worse than it was: when Denver broke the street-occupancy service
      // (2026-07-31, mid-migration), works came back empty and this line
      // took the 1,661 loaded building permits off the map with it.
      this.status = (this._works.length || this._permits.length)
        ? "ready" : "unavailable";
      this.detail = `${this._works.length} sites, ${this._permits.length} permits`;
      return null;
    } catch (error) {
      this.status = "unavailable";
      this.detail = `Unavailable (${error.message})`;
      return null;
    }
  }

  get loadedCount() {
    return (this._works?.length ?? 0) + (this._permits?.length ?? 0);
  }

  legend() {
    return [
      {
        key: "roadwork",
        rgb: palette().roadwork,
        label: "Road work",
        note: "Street occupancy permits active today. Brighter = ends within a week.",
      },
      {
        key: "permit",
        rgb: palette().permit,
        label: "Building permit",
        note: "Construction permits issued in the last four months.",
      },
    ];
  }

  describePermit(permit) {
    const rows = [
      ["Work", permit.kind || "Building permit"],
      ["Issued", asDate(permit.issued)],
    ];
    if (permit.address) rows.push(["Address", permit.address]);
    if (permit.detail) rows.push(["Detail", String(permit.detail).replace(/\//g, " ").trim()]);
    if (permit.cost) rows.push(["Valuation", `$${Number(permit.cost).toLocaleString()}`]);
    if (permit.contractor) rows.push(["Contractor", permit.contractor]);
    if (permit.permit) rows.push(["Permit", permit.permit]);
    // Which city issued it, not a fixed string. This layer carried permits from
    // Denver alone until Aurora was added, and a hardcoded "Denver" would have
    // attributed 1,568 Aurora permits to the wrong government — the kind of
    // wrong that looks right. Older payloads have no `city`, so Denver remains
    // the fallback.
    rows.push(["Source", `${permit.city || "Denver"} building permits`]);
    return { title: permit.address || "Building permit", rows };
  }

  describe(work) {
    const rows = [
      ["Work", work.kind || "—"],
      ["Dates", `${asDate(work.begin)} → ${asDate(work.end)}`],
    ];

    const remaining = Math.ceil((work.end - Date.now()) / DAY_MS);
    if (Number.isFinite(remaining)) {
      rows.push(["Remaining", remaining <= 0 ? "ends today"
        : `${remaining} day${remaining === 1 ? "" : "s"}`]);
    }
    if (work.address) rows.push(["Address", work.address]);
    if (work.from_street && work.to_street) {
      rows.push(["Between", `${work.from_street} and ${work.to_street}`]);
    }
    if (work.permit) rows.push(["Permit", work.permit]);
    rows.push(["Source", "Denver street occupancy permits"]);

    return { title: work.street || work.kind || "Street work", rows };
  }

  /** Supplied by the app: height of the building under a point, or 0. */
  groundHeight = null;

  render() {
    if (!this.enabled || this.status !== "ready") {
      this.visibleCount = 0;
      return [];
    }

    // One permit can cover several street segments; each path is its own row
    // so PathLayer gets the flat list it expects, with the permit carried
    // along for the tooltip.
    const works = this.isHidden("roadwork") ? [] : this._works;
    const permits = this.isHidden("permit") ? [] : (this._permits ?? []);
    const paths = [];
    for (const work of works) {
      for (const path of work.paths ?? []) {
        if (path.length > 1) paths.push({ ...work, path });
      }
    }
    this.visibleCount = works.length + permits.length;

    const soon = Date.now() + ENDING_SOON_DAYS * DAY_MS;

    return [
      new ScatterplotLayer({
        id: `${this.key}-permits`,
        data: permits,
        // A building permit is filed against an address, which puts the point
        // inside the footprint — at ground level it ends up buried in the
        // extrusion, invisible and unclickable. Lifted to the roof of the
        // building it belongs to, which is also where the work usually is.
        getPosition: (d) => [d.lng, d.lat, lift(this.groundHeight?.(d))],
        updateTriggers: { getPosition: footprints.stamp },
        getRadius: 14,
        radiusUnits: "meters",
        radiusMinPixels: 4,
        radiusMaxPixels: 10,
        stroked: true,
        filled: true,
        lineWidthUnits: "pixels",
        getLineWidth: 1,
        getFillColor: [...palette().permit, 120],
        getLineColor: [...palette().permit, 200],
        pickable: true,
        parameters: OVER_EVERYTHING,
      }),
      new PathLayer({
        id: `${this.key}-lines`,
        data: paths,
        // Just clear of the road surface; co-planar lines tear as the
        // camera moves and there is nothing gained by being exactly flat.
        getPath: (d) => d.path.map(([x, y]) => [x, y, GROUND_LIFT_M]),
        getColor: (d) => [...palette().roadwork, d.end <= soon ? 235 : 150],
        updateTriggers: { getColor: themeName() },
        getWidth: 5,
        widthUnits: "meters",
        widthMinPixels: 2,
        widthMaxPixels: 10,
        capRounded: true,
        jointRounded: true,
        pickable: true,
        parameters: OVER_EVERYTHING,
      }),
    ];
  }
}
